import { useRef, useCallback, useState, useEffect } from 'react';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  threshold?: number; // Distance in pixels to trigger refresh
  maxPull?: number; // Maximum pull distance
  isEnabled?: boolean; // Allow disabling the feature
}

interface UsePullToRefreshReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  pullDistance: number;
  isRefreshing: boolean;
  isPulling: boolean;
}

export function usePullToRefresh({
  onRefresh,
  threshold = 80,
  maxPull = 120,
  isEnabled = true,
}: UsePullToRefreshOptions): UsePullToRefreshReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);

  const startYRef = useRef(0);
  const currentYRef = useRef(0);
  const isPullingRef = useRef(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!isEnabled || isRefreshing) return;

    const container = containerRef.current;
    if (!container) return;

    // Only start pull if at the top of the scroll container
    const scrollTop = container.scrollTop;
    if (scrollTop > 0) return;

    startYRef.current = e.touches[0].clientY;
    currentYRef.current = e.touches[0].clientY;
  }, [isEnabled, isRefreshing]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isEnabled || isRefreshing) return;
    if (startYRef.current === 0) return;

    const container = containerRef.current;
    if (!container) return;

    // Check if we're at the top
    const scrollTop = container.scrollTop;
    if (scrollTop > 0) {
      // Reset if user scrolled down
      startYRef.current = 0;
      setPullDistance(0);
      setIsPulling(false);
      isPullingRef.current = false;
      return;
    }

    currentYRef.current = e.touches[0].clientY;
    const diff = currentYRef.current - startYRef.current;

    if (diff > 0) {
      // Pulling down - apply resistance curve for natural feel
      const resistance = 0.5;
      const adjustedDiff = Math.min(diff * resistance, maxPull);

      if (!isPullingRef.current && adjustedDiff > 10) {
        isPullingRef.current = true;
        setIsPulling(true);
      }

      if (isPullingRef.current) {
        // Prevent default scroll behavior when pulling
        e.preventDefault();
        setPullDistance(adjustedDiff);
      }
    }
  }, [isEnabled, isRefreshing, maxPull]);

  const handleTouchEnd = useCallback(async () => {
    if (!isEnabled || !isPullingRef.current) {
      startYRef.current = 0;
      return;
    }

    const finalPull = pullDistance;

    if (finalPull >= threshold && !isRefreshing) {
      // Trigger refresh
      setIsRefreshing(true);
      setPullDistance(threshold * 0.6); // Keep some pull visible during refresh

      try {
        await onRefresh();
      } catch (error) {
        console.error('Pull to refresh error:', error);
      } finally {
        setIsRefreshing(false);
      }
    }

    // Reset
    setPullDistance(0);
    setIsPulling(false);
    isPullingRef.current = false;
    startYRef.current = 0;
  }, [isEnabled, pullDistance, threshold, isRefreshing, onRefresh]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isEnabled) return;

    // Use passive: false for touchmove to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isEnabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    containerRef,
    pullDistance,
    isRefreshing,
    isPulling,
  };
}
